/**
 * The version bar for a descriptor card, rendered above its sections.
 *
 * The chrome is the 204's — status bar, versions dialog, sign dialog — driven
 * by whichever signature boxes the form's descriptor declares. The 202
 * therefore offers Prepared by then Approved by Incident Commander, while the
 * 203 and 208 offer Prepared by alone, with no per-form code.
 */

import type React from 'react';
import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { selectUser } from 'slices/commonSelectors';
import Ics204SignDialog from '../planningView/widgets/AssignmentList/Ics204SignDialog';
import Ics204VersionStatusBar from '../planningView/widgets/AssignmentList/Ics204VersionStatusBar';
import Ics204VersionsDialog, {
  type WalaVersionRow,
} from '../planningView/widgets/AssignmentList/Ics204VersionsDialog';
import { parseWalaSignatures } from '../planningView/widgets/AssignmentList/ics204SignatureSlots';
import {
  useAppendFormSignature,
  useCreateSignedFormVersion,
  useCreateUnsignedFormVersion,
  useFormVersions,
  useFormVersionTip,
} from '../hooks/icsFormVersionHooks';

import {
  buildFormCompletion,
  ICS204_PREPARED_BY_SIGN_ROLE,
  nextOpenSlot,
} from './formSignatureCompletion';

export type FormVersioningPanelProps = {
  baseId: string;
  periodNumber: number;
  /** Hides the create actions when the user cannot edit the incident. */
  readOnly?: boolean;
  /**
   * Why signing is not available yet, for forms with a precondition beyond
   * being editable. The IAP cover cannot be signed until the plan it covers has
   * been generated; the reason is shown in place of the sign action rather than
   * leaving the user to discover it from the server's refusal.
   */
  signDisabledReason?: string | null;
  /**
   * Runs before a signature is recorded; returning false abandons it.
   *
   * For the ICS-207, whose box 4 is a picture of the org chart rather than a set of filled seats: the chart has to be
   * captured and stored before the version snapshot is taken, or the signed form would freeze a reference to nothing.
   */
  onBeforeSign?: () => Promise<boolean>;
  /** Called when the user opens or leaves a historical version. */
  onViewVersionChange?: (versionId: string | null) => void;
  /**
   * Which chain of the form to show, for a form with more than one per period.
   *
   * Only the 225 passes it: an incident carries one rating per person, each
   * signed by that person and their supervisor, so the rating's id scopes the
   * chain. Every other form has the single chain its period implies.
   */
  formInstanceId?: string;
};

export const FormVersioningPanel: React.FC<FormVersioningPanelProps> = ({
  baseId,
  periodNumber,
  readOnly = false,
  signDisabledReason = null,
  onBeforeSign,
  onViewVersionChange,
  formInstanceId,
}) => {
  const user = useSelector(selectUser);
  const defaultSignName =
    user?.profile_settings?.userName?.trim() || user?.user_name?.trim() || '';
  const { data: tip } = useFormVersionTip(
    baseId,
    periodNumber,
    formInstanceId
  );
  const { data: versions = [] } = useFormVersions(baseId, tip?.formInstanceId);

  const [dialog, setDialog] = useState<'all' | 'signed' | null>(null);
  const [signingRole, setSigningRole] = useState<string | null>(null);
  const [viewingId, setViewingId] = useState<string | null>(null);

  const createSigned = useCreateSignedFormVersion(
    baseId,
    periodNumber,
    formInstanceId
  );
  const createDraft = useCreateUnsignedFormVersion(
    baseId,
    periodNumber,
    formInstanceId
  );
  const appendSignature = useAppendFormSignature(
    baseId,
    periodNumber,
    formInstanceId
  );

  const slots = tip?.signatureSlots ?? [];
  const signatures = useMemo(
    () => parseWalaSignatures(tip?.signatures),
    [tip?.signatures]
  );

  const getCompletion = useMemo(
    () => (candidate: Parameters<typeof buildFormCompletion>[1]) =>
      buildFormCompletion(slots, candidate),
    [slots]
  );

  const completion = getCompletion(signatures);
  const openSlot = nextOpenSlot(completion);

  const signedCount = useMemo(
    () =>
      versions.filter(
        (version) =>
          getCompletion(parseWalaSignatures(version.signatures)).status ===
          'signed'
      ).length,
    [versions, getCompletion]
  );

  if (!tip) return null;

  const createItems = readOnly
    ? []
    : [
        ...(completion.status === 'draft' && openSlot
          ? [
              {
                key: 'signed',
                label: signDisabledReason
                  ? `Signed version — ${signDisabledReason}`
                  : `Signed version (${openSlot.label})`,
                onSelect: () => setSigningRole(ICS204_PREPARED_BY_SIGN_ROLE),
                disabled: !!signDisabledReason,
              },
            ]
          : []),
        ...(completion.status !== 'draft'
          ? [
              {
                key: 'draft',
                label: 'New draft version',
                onSelect: () => createDraft.mutate(),
                disabled: createDraft.isPending,
              },
            ]
          : []),
        // Approvals land on the signed tip rather than opening a new version.
        ...(completion.status === 'partially-signed' && openSlot
          ? [
              {
                key: 'approve',
                label: signDisabledReason
                  ? `Sign as ${openSlot.label} — ${signDisabledReason}`
                  : `Sign as ${openSlot.label}`,
                onSelect: () => setSigningRole(openSlot.role),
                disabled: appendSignature.isPending || !!signDisabledReason,
              },
            ]
          : []),
      ];

  const signingSlot = slots.find((slot) => slot.role === signingRole);
  const isPreparedBy = signingRole === ICS204_PREPARED_BY_SIGN_ROLE;

  const handleView = (version: WalaVersionRow) => {
    setDialog(null);
    const next = version.id === tip.id ? null : version.id;
    setViewingId(next);
    onViewVersionChange?.(next);
  };

  const handleViewLatest = () => {
    setViewingId(null);
    onViewVersionChange?.(null);
  };

  const handleSign = async (name: string) => {
    if (!signingRole) return;
    if (onBeforeSign && !(await onBeforeSign())) return;
    const signature = { name, role: signingRole, signedAt: Date.now() };
    if (isPreparedBy) createSigned.mutate(signature);
    else appendSignature.mutate(signature);
    setSigningRole(null);
  };

  return (
    <>
      <Ics204VersionStatusBar
        signatures={signatures}
        viewingPast={viewingId != null}
        isCreatingSignedVersion={false}
        historyCount={versions.length}
        signedCount={signedCount}
        createItems={createItems}
        updatedAt={tip.updatedAt}
        updatedByUserName={tip.updatedByUserName}
        onOpenHistory={() => setDialog('all')}
        onOpenSigned={() => setDialog('signed')}
        onViewLatest={handleViewLatest}
        onCancelCreateSigned={handleViewLatest}
        getCompletion={getCompletion}
      />

      <Ics204VersionsDialog
        open={dialog != null}
        kind={dialog ?? 'all'}
        versions={versions}
        tipId={tip.id}
        onClose={() => setDialog(null)}
        onView={handleView}
        getCompletion={getCompletion}
      />

      <Ics204SignDialog
        open={signingRole != null}
        role={signingRole ?? ''}
        title={
          signingSlot ? `Sign — ${signingSlot.label}` : 'Sign this version'
        }
        description={
          isPreparedBy
            ? 'Signing freezes this version and starts a new draft for further edits.'
            : `Approve this version as ${signingSlot?.label ?? signingRole}.`
        }
        defaultName={defaultSignName}
        isPending={createSigned.isPending || appendSignature.isPending}
        onClose={() => setSigningRole(null)}
        onConfirm={(name) => void handleSign(name)}
      />
    </>
  );
};

export default FormVersioningPanel;
